import { auth } from '../config/firebase';
import { logError, logInfo, logSystemEvent } from './logs';

const getBackendApiUrl = () => process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const HTTP_STATUS = {
	REQUEST_TIMEOUT: 408,
	TOO_MANY_REQUESTS: 429,
	BAD_REQUEST: 400,
	UNAUTHORIZED: 401,
	FORBIDDEN: 403,
	UNPROCESSABLE_ENTITY: 422,
	INTERNAL_SERVER_ERROR: 500,
	BAD_GATEWAY: 502,
	SERVICE_UNAVAILABLE: 503,
	GATEWAY_TIMEOUT: 504,
};

export const API_ERROR_TYPES = {
	NETWORK_ERROR: 'NETWORK_ERROR',
	TIMEOUT_ERROR: 'TIMEOUT_ERROR',
	AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
	AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
	VALIDATION_ERROR: 'VALIDATION_ERROR',
	SERVER_ERROR: 'SERVER_ERROR',
	CLIENT_ERROR: 'CLIENT_ERROR',
	UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

const RETRY_CONFIG = {
	maxRetries: 3,
	baseDelay: 1000,
	maxDelay: 10000,
	jitterFactor: 0.1, // 10% jitter
	requestTimeout: 30000, // 30 seconds
	retryableStatusCodes: [
		HTTP_STATUS.REQUEST_TIMEOUT,
		HTTP_STATUS.TOO_MANY_REQUESTS,
		HTTP_STATUS.INTERNAL_SERVER_ERROR,
		HTTP_STATUS.BAD_GATEWAY,
		HTTP_STATUS.SERVICE_UNAVAILABLE,
		HTTP_STATUS.GATEWAY_TIMEOUT,
	],
	retryableErrors: ['NETWORK_ERROR', 'TIMEOUT_ERROR'],
};

const AUTH_RETRY_CONFIG = {
	maxRetries: 2,
	baseDelay: 500,
	maxDelay: 2000,
	jitterFactor: 0.1,
	requestTimeout: 10000, // 10 seconds for auth requests
	retryableStatusCodes: [
		HTTP_STATUS.REQUEST_TIMEOUT,
		HTTP_STATUS.TOO_MANY_REQUESTS,
		HTTP_STATUS.INTERNAL_SERVER_ERROR,
		HTTP_STATUS.BAD_GATEWAY,
		HTTP_STATUS.SERVICE_UNAVAILABLE,
		HTTP_STATUS.GATEWAY_TIMEOUT,
	],
	retryableErrors: ['NETWORK_ERROR', 'TIMEOUT_ERROR'],
};

export class ApiError extends Error {
	constructor(message, type = API_ERROR_TYPES.UNKNOWN_ERROR, status = null, details = {}) {
		super(message);
		this.name = 'ApiError';
		this.type = type;
		this.status = status;
		this.details = details;
		this.timestamp = new Date().toISOString();
	}
}

function determineErrorType(error, response = null) {
	if (error instanceof ApiError) {
		return error.type;
	}

	if (!navigator.onLine) {
		return API_ERROR_TYPES.NETWORK_ERROR;
	}

	if (isTimeoutError(error)) {
		return API_ERROR_TYPES.TIMEOUT_ERROR;
	}

	if (response) {
		return determineErrorTypeFromResponse(response);
	}

	if (isNetworkError(error)) {
		return API_ERROR_TYPES.NETWORK_ERROR;
	}

	return API_ERROR_TYPES.UNKNOWN_ERROR;
}

function isTimeoutError(error) {
	return error && (error.name === 'AbortError' || error.message?.includes('timeout'));
}

function determineErrorTypeFromResponse(response) {
	if (response.status === HTTP_STATUS.UNAUTHORIZED) {
		return API_ERROR_TYPES.AUTHENTICATION_ERROR;
	}
	if (response.status === HTTP_STATUS.FORBIDDEN) {
		return API_ERROR_TYPES.AUTHORIZATION_ERROR;
	}
	if (
		response.status === HTTP_STATUS.UNPROCESSABLE_ENTITY ||
		response.status === HTTP_STATUS.BAD_REQUEST
	) {
		return API_ERROR_TYPES.VALIDATION_ERROR;
	}
	if (response.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
		return API_ERROR_TYPES.SERVER_ERROR;
	}
	if (response.status >= HTTP_STATUS.BAD_REQUEST) {
		return API_ERROR_TYPES.CLIENT_ERROR;
	}
	return API_ERROR_TYPES.UNKNOWN_ERROR;
}

function isNetworkError(error) {
	return error.message && (error.message.includes('fetch') || error.message.includes('network'));
}

function isAuthRequest(url) {
	return url.includes('/api/auth/') || url.includes('/auth/');
}

function getRetryConfig(url) {
	return isAuthRequest(url) ? AUTH_RETRY_CONFIG : RETRY_CONFIG;
}

function shouldRetry(error, attempt, response = null, url = '') {
	const config = getRetryConfig(url);

	if (attempt >= config.maxRetries) {
		return false;
	}

	if (response && config.retryableStatusCodes.includes(response.status)) {
		return true;
	}

	const errorType = determineErrorType(error, response);
	return config.retryableErrors.includes(errorType);
}

function calculateRetryDelay(attempt, url = '') {
	const config = getRetryConfig(url);
	const delay = config.baseDelay * Math.pow(2, attempt - 1);
	const jitter = Math.random() * config.jitterFactor * delay;
	return Math.min(delay + jitter, config.maxDelay);
}

function sleep(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}

async function makeRequest(url, options = {}, attempt = 1) {
	const config = getRetryConfig(url);
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), config.requestTimeout);

	try {
		const response = await fetch(url, {
			...options,
			// Ensure auth cookies (e.g., TOTP session) are sent/received
			credentials: 'include',
			signal: controller.signal,
		});

		clearTimeout(timeoutId);

		if (!response.ok) {
			return await handleUnsuccessfulResponse(response, url, options, attempt);
		}

		return response;
	} catch (error) {
		clearTimeout(timeoutId);
		return handleRequestError(error, url, options, attempt);
	}
}

async function handleUnsuccessfulResponse(response, url, options, attempt) {
	let errorData = {};
	try {
		errorData = await response.json();
	} catch (parseError) {
		logInfo('Failed to parse error response body; using status fallback', {
			url,
			status: response.status,
			error: parseError instanceof Error ? parseError.message : String(parseError),
		});
		errorData = { message: response.statusText || 'Request failed' };
	}

	const errorType = determineErrorType(null, response);
	const errorMessage = buildErrorMessage(errorData, response);

	const apiError = new ApiError(errorMessage, errorType, response.status, errorData);

	if (shouldRetry(apiError, attempt, response, url)) {
		return retryRequest(url, options, attempt, apiError);
	}

	throw apiError;
}

function buildErrorMessage(errorData, response) {
	let errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
	if (errorData.validationErrors && errorData.validationErrors.length > 0) {
		const validationMessages = errorData.validationErrors.map(err => err.message).join('; ');
		errorMessage = `Validation failed: ${validationMessages}`;
	}
	return errorMessage;
}

async function retryRequest(url, options, attempt, error) {
	const delay = calculateRetryDelay(attempt, url);
	logInfo(`Retrying request after ${delay}ms`, {
		attempt,
		url,
		status: error.status,
		delay,
	});

	await sleep(delay);
	return makeRequest(url, options, attempt + 1);
}

function handleRequestError(error, url, options, attempt) {
	if (!navigator.onLine) {
		return handleNetworkError(error, url, options, attempt);
	}

	if (error.name === 'AbortError' || isTimeoutError(error)) {
		return handleTimeoutError(error, url, options, attempt);
	}

	if (isNetworkError(error)) {
		return handleNetworkError(error, url, options, attempt);
	}

	if (error instanceof ApiError) {
		throw error;
	}

	throw new ApiError(
		(error instanceof Error ? error.message : error) || 'An unexpected error occurred',
		API_ERROR_TYPES.UNKNOWN_ERROR,
		null,
		{ originalError: error instanceof Error ? error.message : error }
	);
}

async function handleNetworkError(error, url, options, attempt) {
	const networkError = new ApiError(
		'Network error occurred',
		API_ERROR_TYPES.NETWORK_ERROR,
		null,
		{ originalError: error.message }
	);

	if (shouldRetry(networkError, attempt, null, url)) {
		const delay = calculateRetryDelay(attempt, url);
		logInfo(`Retrying request after network error: ${delay}ms`, {
			attempt,
			url,
			delay,
		});

		await sleep(delay);
		return makeRequest(url, options, attempt + 1);
	}

	throw networkError;
}

async function handleTimeoutError(error, url, options, attempt) {
	const timeoutError = new ApiError('Request timed out', API_ERROR_TYPES.TIMEOUT_ERROR, null, {
		originalError: error.message,
	});

	if (shouldRetry(timeoutError, attempt, null, url)) {
		const delay = calculateRetryDelay(attempt, url);
		logInfo(`Retrying request after timeout: ${delay}ms`, {
			attempt,
			url,
			delay,
		});

		await sleep(delay);
		return makeRequest(url, options, attempt + 1);
	}

	throw timeoutError;
}

const handleApiError = (error, _options, _url, _startTime, _duration) => {
	if (error instanceof ApiError) {
		throw new Error(`API request failed: ${error.message}`);
	}
	const errorMessage = error instanceof Error ? error.message : String(error);
	throw new Error(`API request failed: ${errorMessage}`);
};

const makeApiCall = async (url, options, fullUrl, startTime) => {
	logInfo(`API Request: ${options.method || 'GET'} ${url}`, {
		method: options.method || 'GET',
		url: fullUrl,
		timestamp: new Date().toISOString(),
	});

	const response = await makeRequest(fullUrl, {
		method: options.method || 'GET',
		...options,
	});

	const data = await handleResponse(response);

	const duration = performance.now() - startTime;
	logInfo(`API Response: ${options.method || 'GET'} ${url} - ${response.status}`, {
		method: options.method || 'GET',
		url: fullUrl,
		status: response.status,
		duration: Math.round(duration),
		timestamp: new Date().toISOString(),
	});

	return data;
};

const retryAfterUnauthorized = async (url, retryableOptions, startTime) => {
	logSystemEvent('authenticated_request_received_401', 'warning', {
		url,
		attemptingRefresh: true,
	});

	try {
		const refreshedToken = await auth.currentUser?.getIdToken?.(true);
		if (!refreshedToken) {
			logSystemEvent('401_refresh_token_unavailable', 'warning', {
				url,
				hasCurrentUser: !!auth.currentUser,
			});
			return null;
		}

		logInfo('401 token refresh succeeded; retrying request', {
			url,
		});

		const retryHeaders = {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${refreshedToken}`,
			...retryableOptions.headers,
		};

		const fullUrl = `${getBackendApiUrl()}${url}`;
		return await makeApiCall(
			url,
			{ ...retryableOptions, headers: retryHeaders },
			fullUrl,
			startTime
		);
	} catch (refreshError) {
		logInfo('401 retry failed during token refresh', {
			url,
			error: refreshError instanceof Error ? refreshError.message : String(refreshError),
		});
		logSystemEvent('401_refresh_failed', 'warning', {
			url,
			error: refreshError instanceof Error ? refreshError.message : String(refreshError),
		});
		return null;
	}
};

export const makeAuthenticatedRequest = async (url, options = {}) => {
	const startTime = performance.now();
	const retryableOptions = { ...options };

	try {
		if (!auth.currentUser) {
			logSystemEvent('authenticated_request_without_current_user', 'warning', {
				url,
			});
			throw new Error('No authenticated Firebase user available');
		}

		const firebaseIdToken = await auth.currentUser.getIdToken();

		const headers = {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${firebaseIdToken}`,
			...retryableOptions.headers,
		};

		const fullUrl = `${getBackendApiUrl()}${url}`;

		const response = await makeApiCall(
			url,
			{ ...retryableOptions, headers },
			fullUrl,
			startTime
		);

		return response;
	} catch (error) {
		if (error instanceof ApiError && error.status === HTTP_STATUS.UNAUTHORIZED) {
			const retryResult = await retryAfterUnauthorized(url, retryableOptions, startTime);
			if (retryResult) {
				return retryResult;
			}
		}

		handleApiError(error, options, url, startTime);
		return undefined;
	}
};

export const handleResponse = async response => {
	const noBody =
		response.status === 204 ||
		response.status === 205 ||
		response.headers?.get('Content-Length') === '0';

	if (noBody) {
		return null;
	}

	try {
		const responseData = await response.json();

		if (!response.ok) {
			logSystemEvent(`API Error: ${response.status} - ${response.statusText}`, 'error', {
				status: response.status,
				statusText: response.statusText,
				url: response.url,
				responseData,
				timestamp: new Date().toISOString(),
			});

			throw new Error(
				responseData.message || `API Error: ${response.status} - ${response.statusText}`
			);
		}

		return responseData;
	} catch (parseError) {
		if (parseError?.message?.includes('API Error')) {
			throw parseError;
		}

		logError('Failed to parse API response', parseError, {
			status: response?.status,
			statusText: response?.statusText,
			url: response?.url,
			timestamp: new Date().toISOString(),
		});

		const errorMessage = parseError?.message || 'Unknown parsing error';
		throw new Error(`Failed to parse response: ${errorMessage}`);
	}
};
